import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { makeStyles } from '@mui/styles';
import React from 'react';

import { DataAPIContext } from '../api';
import { UPDATE } from '../api/dataProvider';
import SandboxedHtmlPreview from '../components/SandboxedHtmlPreview';

const useStyles = makeStyles((theme) => ({
    resultPane: {
        background: '#f7f7f7',
        border: '1px solid #ddd',
        padding: theme.spacing(2),
        maxHeight: 400,
        overflow: 'auto',
        fontFamily: 'monospace',
        fontSize: 13,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
}));

const FORMAT_OPTIONS = [
    { value: 'html', label: 'HTML' },
    { value: 'markdown', label: 'Markdown' },
    { value: 'plaintext', label: 'Plain text' },
];

const DEFAULT_EVENT_CODE = 'tour_participant.created';
const DEFAULT_SOURCE_DATA = '{"gtu": 0}';

const TestRenderDialog = ({ open, onClose, notificationStringId, draftBody, initialFormat }) => {
    const classes = useStyles();
    const dataAPI = React.useContext(DataAPIContext);

    const [eventCode, setEventCode] = React.useState(DEFAULT_EVENT_CODE);
    const [sourceDataText, setSourceDataText] = React.useState(DEFAULT_SOURCE_DATA);
    const [format, setFormat] = React.useState(initialFormat || 'html');
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState(null);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (open) {
            setResult(null);
            setError(null);
            if (initialFormat) {
                setFormat(initialFormat);
            }
        }
    }, [open, initialFormat]);

    const onRender = async () => {
        setError(null);
        setResult(null);

        let sourceData;
        try {
            sourceData = JSON.parse(sourceDataText);
        } catch (e) {
            setError(`Source data is not valid JSON: ${e.message}`);
            return;
        }
        if (typeof sourceData !== 'object' || sourceData === null || Array.isArray(sourceData)) {
            setError('Source data must be a JSON object');
            return;
        }

        setLoading(true);
        try {
            const res = await dataAPI(UPDATE, 'notification_string', {
                id: notificationStringId,
                urlSuffix: 'preview/',
                data: {
                    event_code: eventCode,
                    source_data: sourceData,
                    format,
                    body_override: draftBody || undefined,
                },
            });
            if (res.statusCode === 200) {
                setResult(res.data);
            } else {
                setError(res.data?.message || `Render failed (status ${res.statusCode})`);
            }
        } catch (e) {
            setError(`Request failed: ${e.message || e}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Test Render</DialogTitle>
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                            Render this notification's current draft against a sample event source. Nothing is saved or sent.
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Event code"
                            variant="filled"
                            size="small"
                            fullWidth
                            value={eventCode}
                            onChange={(e) => setEventCode(e.target.value)}
                            helperText="e.g. tour_participant.created"
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl variant="filled" size="small" fullWidth>
                            <InputLabel>Format</InputLabel>
                            <Select value={format} onChange={(e) => setFormat(e.target.value)} label="Format">
                                {FORMAT_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Source data (JSON)"
                            variant="filled"
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={6}
                            value={sourceDataText}
                            onChange={(e) => setSourceDataText(e.target.value)}
                            helperText='e.g. {"gtu": 12345} for tour_participant.* events'
                        />
                    </Grid>

                    {error && (
                        <Grid item xs={12}>
                            <Alert severity="error">{error}</Alert>
                        </Grid>
                    )}

                    {result && (
                        <>
                            {result.context_resolved === false && (
                                <Grid item xs={12}>
                                    <Alert severity="warning">
                                        Some context fields could not be fully resolved (lazy-evaluated fields).
                                        These would resolve at real fire time but show as placeholders here.
                                    </Alert>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Rendered output ({result.format})
                                </Typography>
                                {result.format === 'html' ? (
                                    <SandboxedHtmlPreview
                                        html={result.rendered}
                                        title="Rendered notification preview"
                                        style={{ height: 400 }}
                                    />
                                ) : (
                                    <Box className={classes.resultPane}>{result.rendered}</Box>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Source ({result.format})
                                </Typography>
                                <Box className={classes.resultPane}>{result.rendered}</Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PlayArrowIcon />}
                    onClick={onRender}
                    disabled={loading || !eventCode || !sourceDataText}
                >
                    {loading ? 'Rendering…' : 'Render'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TestRenderDialog;
